import React, { useState } from 'react';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@cvent/blocks/hooks/useTheme';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { useTranslate } from 'nucleus-text';
import Link from 'next/link';
import { useAttendeeMessaging } from '@context/AttendeeMessagingContext';
import { useRouter } from 'next/router';
import Alert from '@illustrations/Alert';
import ErrorState from '@components/shared/ErrorState';
import Button from '@cvent/carina/components/Button';
import { CSSObject } from '@emotion/react';
import NavigationFlyoutControl from '@components/header/flyouts/NavigationFlyoutControl';
import { useAttendeeHubConfig } from '@hooks/useAttendeeHubConfig';
import { EditNewIcon } from '@components/shared/V1ToV2Icon';
import Typography from '@cvent/blocks/components/Typography';
import ChannelList from './ChannelList';
import MessageIcon from '../MessageIcon';
import EmptyState from '../EmptyState';

const useStyles = (): Record<string, CSSObject> => {
  const { isMobile } = useBreakpoints();
  const { backgroundColor, font, depth, size } = useTheme();
  const { xOffset, yOffset, blurRadius, spreadRadius, color } = depth.boxShadow.hard;
  return {
    container: {
      width: '496px',
      padding: '24px',
      borderRadius: size.border.radius.fiveX,
      boxShadow: `${xOffset} ${yOffset} ${blurRadius} ${spreadRadius} ${color}`
    },
    mobileTextLink: {
      cursor: 'pointer',
      textDecoration: 'none',
      padding: 0,
      flexGrow: 1,
      flexBasis: 'auto',
      marginTop: 0,
      position: 'relative',
      border: 'none',
      borderRadius: 10,
      color: 'initial'
    },
    triggerButton: {
      textDecoration: 'none',
      cursor: 'pointer',
      borderRadius: 10,
      padding: '10px 13px 9px 13px',
      position: 'relative',
      border: 'none',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: backgroundColor.brand.hover
      },
      '&:focus': {
        backgroundColor: backgroundColor.brand.focus,
        // imitating Chrome default focus outline in Firefox
        '@-moz-document url-prefix()': {
          outline: '2px solid blue',
          boxShadow: '0 0 0 3px white'
        }
      }
    },
    headerContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16
    },
    headerContainerWithButton: {
      marginBottom: 0
    },
    flyoutHeader: {
      margin: 0
    },
    flyoutHeaderWithButton: {
      paddingBottom: 14
    },
    icon: {
      color: !isMobile ? font.color.base : font.color.neutral.base
    },
    buttonWrapper: {
      display: 'flex',
      flex: '1 1 auto',
      justifyContent: 'center',
      padding: '32px 0 0'
    }
  };
};

function MessagingFlyoutContent({
  children,
  eventId,
  hasLoadedChannels,
  isAttendeeListEnabled,
  onClose
}: React.PropsWithChildren<{
  eventId: string;
  hasLoadedChannels: boolean;
  isAttendeeListEnabled: boolean;
  onClose: () => void;
}>): JSX.Element {
  const styles = useStyles();
  const { translate } = useTranslate();
  return (
    <div {...injectTestId('attendee-messaging-flyout')} css={styles.container}>
      <div
        css={{
          ...styles.headerContainer,
          ...(hasLoadedChannels && isAttendeeListEnabled && styles.headerContainerWithButton)
        }}
      >
        <Typography
          as="h2"
          styledAs="h3"
          css={{
            ...styles.flyoutHeader,
            ...(hasLoadedChannels && isAttendeeListEnabled && styles.flyoutHeaderWithButton)
          }}
          {...injectTestId('attendee-messaging-nav-flyout-title')}
        >
          {translate('AttendeeMessaging-Title-Messages')}
        </Typography>
        {hasLoadedChannels && isAttendeeListEnabled && (
          <Link href={`/events/${eventId}/messages?newChannel=true`} passHref legacyBehavior>
            <Button
              icon={EditNewIcon}
              appearance="ghost"
              accessibilityLabel={translate('AttendeeMessaging-Empty-Button-Send-Message')}
              text={translate('AttendeeMessaging-Empty-Button-Send-Message')}
              element="a"
              testID="attendee-messaging-flyout-empty-state-new-message-button-with-icon"
              onClick={() => onClose()}
            />
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

function MessagingFlyout(): JSX.Element {
  const { isMobile } = useBreakpoints();
  const styles = useStyles();
  const { translate } = useTranslate();
  const { client, streamClientError } = useAttendeeMessaging();
  const [hasLoadedChannels, setHasLoadedChannels] = useState(false);
  const router = useRouter();
  const {
    query: { eventId },
    pathname
  } = router;
  const { isFeatureSettingEnabled } = useAttendeeHubConfig();
  const isAttendeeListEnabled = isFeatureSettingEnabled('ATTENDEE_LIST');
  const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);
  const closeFlyout = () => {
    setIsFlyoutVisible(false);
  };

  // No flyout when attendees are on mobile viewport or on the messaging page.
  if (isMobile || pathname.endsWith('messages')) {
    if (isFlyoutVisible) {
      closeFlyout();
    }
    return (
      <Link
        href={`/events/${eventId}/messages`}
        passHref
        {...injectTestId('message-nav-mobile-link')}
        css={isMobile ? styles.mobileTextLink : styles.triggerButton}
        aria-label={translate('AttendeeMessaging-MessagesNavLink-AccessibilityLabel')}
      >
        <MessageIcon iconOnly={!isMobile} />
      </Link>
    );
  }

  let content;
  if (streamClientError) {
    content = (
      <ErrorState
        title={translate('unexpected_error_title')}
        subtitle={translate('unexpected_error_notified')}
        image={<Alert />}
        hideAdditionalInfo
        testID="attendee-messaging-flyout-stream-connection-error"
      />
    );
  } else if (client) {
    content = <ChannelList setHasLoadedChannels={setHasLoadedChannels} onNavFlyoutClose={closeFlyout} flyoutStyle />;
  } else {
    content = (
      <div {...injectTestId('attendee-messaging-flyout-empty-container')}>
        <EmptyState flyoutStyle />
        {isAttendeeListEnabled ? (
          <div css={styles.buttonWrapper}>
            <Link href={`/events/${eventId}/messages`} passHref legacyBehavior>
              <Button
                appearance="filled"
                accessibilityLabel={translate('AttendeeMessaging-Empty-Button-Send-Message')}
                text={translate('AttendeeMessaging-Empty-Button-Send-Message')}
                element="a"
                testID="attendee-messaging-flyout-empty-state-new-message-button"
                onClick={closeFlyout}
              />
            </Link>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <NavigationFlyoutControl
      accessibilityLabel={translate('AttendeeMessaging-MessagesNavLink-AccessibilityLabel')}
      icon={<MessageIcon iconOnly isFlyoutVisible={isFlyoutVisible} />}
      testID="message-icon"
      isFlyoutVisible={isFlyoutVisible}
      onFlyoutVisible={setIsFlyoutVisible}
    >
      <MessagingFlyoutContent
        isAttendeeListEnabled={isAttendeeListEnabled}
        eventId={String(eventId)}
        hasLoadedChannels={hasLoadedChannels}
        onClose={closeFlyout}
      >
        {content}
      </MessagingFlyoutContent>
    </NavigationFlyoutControl>
  );
}

export default MessagingFlyout;
